<!--
  NotesField — free text. Auto-grows so a long note never hides itself behind
  a scrollbar the size of a fingernail.

  Collapsed by default in repeating rows: a "Notes" affordance that shows the
  first line when something has been written, so a full chute-test table stays
  readable on a phone while never hiding recorded content.
-->
<script lang="ts">
  interface Props {
    label?: string;
    value: string | null;
    placeholder?: string;
    rows?: number;
    disabled?: boolean;
    hideLabel?: boolean;
    /** Render as a tap-to-open row affordance instead of an always-open box. */
    collapsible?: boolean;
    onValue?: (v: string | null) => void;
    onCommit?: (v: string | null) => void;
  }

  let {
    label = 'Notes',
    value,
    placeholder = '',
    rows = 2,
    disabled = false,
    hideLabel = false,
    collapsible = false,
    onValue,
    onCommit
  }: Props = $props();

  let open = $state(!collapsible);
  let el: HTMLTextAreaElement | null = $state(null);

  const id = `notes-${Math.random().toString(36).slice(2, 9)}`;

  function grow() {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  $effect(() => {
    // re-measure when the value or the open state changes
    void value;
    void open;
    grow();
  });

  function norm(raw: string): string | null {
    return raw.trim() === '' ? null : raw;
  }

  function onInput(e: Event) {
    // Only an EMPTY box is null while typing. Trimming here would make the
    // parent echo back a value without the space just typed, and the character
    // would vanish under the thumb. Whitespace is normalised on commit.
    const raw = (e.currentTarget as HTMLTextAreaElement).value;
    onValue?.(raw === '' ? null : raw);
    grow();
  }
</script>

{#if collapsible && !open}
  <button
    type="button"
    class="peek"
    class:has-note={!!value}
    {disabled}
    onclick={() => {
      open = true;
      queueMicrotask(() => el?.focus());
    }}
  >
    <span class="peek-label">{label}</span>
    <span class="peek-text">{value ?? 'Add note'}</span>
  </button>
{:else}
  <div class="nof">
    <label for={id} class:sr-only={hideLabel}>{label}</label>
    <textarea
      {id}
      bind:this={el}
      {rows}
      {placeholder}
      {disabled}
      value={value ?? ''}
      oninput={onInput}
      onblur={(e) => onCommit?.(norm((e.currentTarget as HTMLTextAreaElement).value))}
    ></textarea>
  </div>
{/if}

<style>
  .nof {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }

  label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--fg-muted, #3f4750);
    line-height: 1.2;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  textarea {
    width: 100%;
    box-sizing: border-box;
    /* 16px minimum so iOS Safari does not zoom the viewport on focus. */
    font: inherit;
    font-size: 1rem;
    line-height: 1.35;
    padding: 0.625rem;
    min-height: 3rem;
    resize: none;
    overflow: hidden;
    border: 2px solid var(--border, #9aa3ad);
    border-radius: 0.5rem;
    background: var(--surface, #fff);
    color: var(--fg, #10151b);
  }

  textarea:focus {
    outline: none;
    border-color: var(--accent, #0b63c5);
    box-shadow: 0 0 0 3px var(--accent-ring, rgba(11, 99, 197, 0.28));
  }

  textarea:disabled {
    background: var(--surface-disabled, #eef1f4);
    color: var(--fg-muted, #3f4750);
    -webkit-text-fill-color: var(--fg-muted, #3f4750);
    opacity: 1;
  }

  .peek {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    width: 100%;
    min-height: 2.75rem;
    padding: 0.5rem 0.625rem;
    text-align: left;
    border: 2px dashed var(--border, #9aa3ad);
    border-radius: 0.5rem;
    background: transparent;
    color: var(--fg-muted, #3f4750);
    font: inherit;
    font-size: 0.9375rem;
    cursor: pointer;
    touch-action: manipulation;
  }

  .peek.has-note {
    border-style: solid;
    background: var(--surface, #fff);
    color: var(--fg, #10151b);
  }

  .peek:focus-visible {
    outline: 3px solid var(--accent, #0b63c5);
    outline-offset: 2px;
  }

  .peek-label {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--fg-muted, #3f4750);
    flex: 0 0 auto;
  }

  .peek-text {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
